import {
  Column,
  DelayedSpinner,
  UploadFileButton
} from '@cdab/scania/qpr/components/atoms'
import { AttachmentList } from '@cdab/scania/qpr/components/molecules'
import {
  useCreateDocument,
  useCreateDocumentWithFile,
  useCssVariableBreakpoint
} from '@cdab/scania/qpr/hooks'
import type { User } from '@cdab/scania/qpr/schema'
import { Roles, type DocumentFile } from '@cdab/scania/qpr/schema'
import { freeMemory } from '@cdab/scania/qpr/utils'
import {
  ValidateUrl,
  capitalizeFirstLetter,
  checkStringLength
} from '@cdab/utils'
import {
  TdsButton,
  TdsDropdown,
  TdsDropdownOption,
  TdsMessage,
  TdsRadioButton,
  TdsTextField,
  TdsToast
} from '@scania/tegel-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import {
  ButtonWrapper,
  SpinnerWrapper,
  StyledButton,
  StyledColumn,
  StyledFirstButton,
  StyledLinkRow,
  StyledMessage,
  StyledRow,
  StyledSectionTitle,
  StyledTextfieldWrapper,
  Title
} from './document-create-form.styles'
import type {
  DocumentCreateFormProps,
  NewAdminDocumentCreateData
} from './document-create-form.types'

export function DocumentCreateForm({
  auditPoints,
  checkPoints,
  allMarkets,
  currentUser
}: DocumentCreateFormProps) {
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const navigate = useNavigate()
  const { t } = useTranslation(['documents', 'common'])

  const [data, setData] = useState<NewAdminDocumentCreateData>({
    title: '',
    auditPointsIds: [],
    checkPointsIds: [],
    marketId: null,
    url: ''
  })

  const [stateDocumentTitle, setStateDocumentTitle] = useState<boolean>(true)
  const [stateDocumentType, setStateDocumentType] = useState('File')
  const [stateAuditPoints, setStateAuditPoints] = useState<boolean>(true)
  const [stateCheckPoints, setStateCheckPoints] = useState<boolean>(true)
  const [stateUrl, setStateUrl] = useState<boolean>(true)

  const [stateFileToUpload, setStateFileToUpload] = useState<
    DocumentFile | undefined
  >(undefined)

  const [
    createUploadDocumentState,
    handleClickUploadCreateDocument,
    setCreateUploadDocumentState
  ] = useCreateDocumentWithFile(
    { ...data, documentFileId: null },
    stateFileToUpload
  )

  const [
    createDocumentState,
    handleClickCreateDocument,
    setCreateDocumentState
  ] = useCreateDocument({
    ...data,
    documentFileId: null
  })

  const isLoading =
    createUploadDocumentState.status === 'uploading' ||
    createDocumentState.status === 'loading'

  const isSuccess =
    createUploadDocumentState.status === 'success' ||
    createDocumentState.status === 'success'

  const shouldShowUploadButton =
    stateDocumentType === 'File' && !stateFileToUpload

  const shouldShowAttachmentsList =
    stateDocumentType === 'File' && stateFileToUpload

  const onDeleteSelectedFile = () => {
    if (!stateFileToUpload) return

    freeMemory(stateFileToUpload)

    setStateFileToUpload(undefined)
  }

  const onUploadFileClick = (fileList: FileList) => {
    const files = Array.from(fileList)

    if (files.length === 0) return

    const inputFiles: DocumentFile[] = Array.from(files).map(file => ({
      file,
      id: uuidv4(),
      url: URL.createObjectURL(file)
    }))

    setStateFileToUpload(inputFiles[0])
  }

  function hasAccess(currentUser: User): boolean {
    return currentUser.role === Roles.CoOrdinator
  }

  const checkAllData = useCallback(() => {
    return (
      checkStringLength(data.title, 4) &&
      (data.auditPointsIds.length > 0 || data.checkPointsIds.length > 0) &&
      (stateDocumentType === 'File'
        ? stateFileToUpload !== undefined
        : ValidateUrl(data.url))
    )
  }, [
    data.title,
    data.auditPointsIds.length,
    data.checkPointsIds.length,
    data.url,
    stateDocumentType,
    stateFileToUpload
  ])

  const validateAllData = useCallback(() => {
    setStateDocumentTitle(checkStringLength(data.title, 4))
    setStateAuditPoints(
      data.auditPointsIds.length > 0 || data.checkPointsIds.length > 0
    )
    setStateCheckPoints(
      data.auditPointsIds.length > 0 || data.checkPointsIds.length > 0
    )
    setStateUrl(ValidateUrl(data.url))

    return checkAllData()
  }, [
    data.title,
    data.auditPointsIds.length,
    data.checkPointsIds.length,
    data.url,
    checkAllData
  ])

  const onChangeAuditPoints = useCallback((value: string | null) => {
    if (value === null) return

    const selectedAuditPointIds = value
      .split(',')
      .filter(id => id.trim() !== '')
      .map(id => parseInt(id.trim()))

    setData(prevData => {
      const hasCheckPoints = prevData.checkPointsIds.length > 0
      setStateAuditPoints(selectedAuditPointIds.length > 0 || hasCheckPoints)
      setStateCheckPoints(selectedAuditPointIds.length > 0 || hasCheckPoints)

      return {
        ...prevData,
        auditPointsIds: selectedAuditPointIds
      }
    })
  }, [])

  const onChangeCheckPoints = useCallback((value: string | null) => {
    if (value === null) return

    const selectedCheckPointIds = value
      .split(',')
      .filter(id => id.trim() !== '')
      .map(id => parseInt(id.trim()))

    setData(prevData => {
      const hasAuditPoints = prevData.auditPointsIds.length > 0
      setStateAuditPoints(selectedCheckPointIds.length > 0 || hasAuditPoints)
      setStateCheckPoints(selectedCheckPointIds.length > 0 || hasAuditPoints)

      return {
        ...prevData,
        checkPointsIds: selectedCheckPointIds
      }
    })
  }, [])

  useEffect(() => {
    return () => {
      freeMemory(stateFileToUpload)
    }
  }, [stateFileToUpload])

  useEffect(() => {
    if (isSuccess) {
      if (createUploadDocumentState.status === 'success') {
        toast(
          <TdsToast
            variant='success'
            header={t('document-created', { ns: 'common' })}
            subheader={createUploadDocumentState.message}
          />
        )
        setCreateUploadDocumentState({ status: 'none', message: null })
      }

      if (createDocumentState.status === 'success') {
        toast(
          <TdsToast
            variant='success'
            header={t('document-created', { ns: 'common' })}
            subheader={createDocumentState.message}
          />
        )
        setCreateDocumentState({ status: 'none', message: null })
      }

      navigate('/admin/documents/all')
    }
  }, [
    createDocumentState,
    createUploadDocumentState,
    isSuccess,
    navigate,
    setCreateDocumentState,
    setCreateUploadDocumentState,
    t
  ])

  return (
    <Column width={12} lg={6} offset={{ lg: 3 }}>
      <StyledRow>
        <Column width={12} lg={6} padding={isLg}>
          <Title>{t('add-new-document', { ns: 'documents' })}</Title>
        </Column>
      </StyledRow>
      <StyledRow>
        <StyledColumn width={12} padding={isLg}>
          <TdsTextField
            label={t('name', { ns: 'common' })}
            labelPosition='outside'
            maxLength={200}
            placeholder={t('add-name-for-document', {
              ns: 'documents'
            })}
            modeVariant='secondary'
            value={data.title}
            onTdsInput={e => {
              setData({ ...data, title: e.target.value })
            }}
            onTdsBlur={e =>
              setStateDocumentTitle(checkStringLength(e.target.value, 4))
            }
            state={!stateDocumentTitle ? 'error' : undefined}
            helper={
              !stateDocumentTitle
                ? t('message.validation-error', { chars: '4' })
                : undefined
            }
          />
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn width={12} lg={6} padding={isLg}>
          <TdsDropdown
            id='auditPointId'
            modeVariant='secondary'
            label={capitalizeFirstLetter(
              t('audit-point', {
                ns: 'common'
              })
            )}
            size='lg'
            labelPosition='outside'
            placeholder={t('select-audit-point', {
              ns: 'documents'
            })}
            defaultValue={data.auditPointsIds.join(',')}
            openDirection={isLg ? 'down' : 'auto'}
            multiselect
            onTdsChange={e => onChangeAuditPoints(e.detail.value)}
            error={!stateAuditPoints}
            helper={
              !stateAuditPoints
                ? t('auditpoint-required', { ns: 'documents' })
                : undefined
            }
          >
            {auditPoints
              .sort((a, b) => a.auditPointNo.localeCompare(b.auditPointNo))
              .map(({ id, auditPointNo }) => {
                return (
                  <TdsDropdownOption key={id} value={id.toString()}>
                    {auditPointNo}
                  </TdsDropdownOption>
                )
              })}
          </TdsDropdown>
        </StyledColumn>
        <StyledColumn width={12} lg={6} padding={isLg}>
          <TdsDropdown
            id='checkPointId'
            label={capitalizeFirstLetter(
              t('checkpoint', {
                ns: 'common'
              })
            )}
            modeVariant='secondary'
            size='lg'
            labelPosition='outside'
            placeholder={t('select-check-point', {
              ns: 'documents'
            })}
            defaultValue={data.checkPointsIds.join(',')}
            openDirection={isLg ? 'down' : 'auto'}
            multiselect
            onTdsChange={e => onChangeCheckPoints(e.detail.value)}
            error={!stateCheckPoints}
            helper={
              !stateCheckPoints
                ? t('checkpoint-required', { ns: 'documents' })
                : undefined
            }
          >
            {checkPoints
              .sort((a, b) => a.checkPointNo.localeCompare(b.checkPointNo))
              .map(({ id, checkPointNo }) => {
                return (
                  <TdsDropdownOption key={id} value={id.toString()}>
                    {checkPointNo}
                  </TdsDropdownOption>
                )
              })}
          </TdsDropdown>
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn width={12} padding={isLg}>
          <TdsDropdown
            id='marketId'
            label={capitalizeFirstLetter(t('market', { ns: 'common' }))}
            size='lg'
            modeVariant='secondary'
            labelPosition='outside'
            placeholder={t('select-market', { ns: 'documents' })}
            defaultValue={'null'}
            openDirection={isLg ? 'down' : 'auto'}
            onTdsChange={e => {
              const option = e.detail.value
              const marketId =
                option === null || option === 'null' ? null : parseInt(option)

              setData({
                ...data,
                marketId: marketId
              })
            }}
          >
            {!hasAccess(currentUser) && (
              <TdsDropdownOption key='factory' value='null'>
                Factory
              </TdsDropdownOption>
            )}
            {allMarkets.map(market => (
              <TdsDropdownOption key={market.id} value={market.id.toString()}>
                {market.marketName}
              </TdsDropdownOption>
            ))}
          </TdsDropdown>
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn width={12} lg={3} padding={isLg}>
          <StyledSectionTitle>
            {t('document-type', { ns: 'documents' })}
          </StyledSectionTitle>
          <TdsRadioButton
            name='document'
            value='File'
            radio-id='document-file'
            required={false}
            checked={stateDocumentType === 'File'}
            onTdsChange={e => setStateDocumentType(e.detail.value)}
          >
            <div slot='label'>{t('file', { ns: 'documents' })}</div>
          </TdsRadioButton>

          <TdsRadioButton
            name='document'
            value='Link'
            radio-id='document-link'
            required={false}
            checked={stateDocumentType === 'Link'}
            onTdsChange={e => setStateDocumentType(e.detail.value)}
          >
            <div slot='label'>{t('link', { ns: 'common' })}</div>
          </TdsRadioButton>
        </StyledColumn>
        <StyledColumn width={12} lg={9} padding={isLg}>
          {shouldShowUploadButton ? (
            <UploadFileButton
              className='tds-u-mt3'
              size='sm'
              variant='secondary'
              multiple={false}
              onUploadFiles={onUploadFileClick}
            />
          ) : shouldShowAttachmentsList ? (
            <AttachmentList
              modeVariant='secondary'
              fileType='all'
              files={[
                {
                  fileName: stateFileToUpload.file.name,
                  id: stateFileToUpload.id,
                  isImage: false,
                  isUploaded: createUploadDocumentState.status === 'success',
                  uploadProgress:
                    createUploadDocumentState.status === 'uploading'
                      ? createUploadDocumentState.progress
                      : createUploadDocumentState.status === 'success'
                      ? 100
                      : 0,
                  url: stateFileToUpload.url
                }
              ]}
              hideUploadProgress={false}
              onDeleteAttachment={onDeleteSelectedFile}
              disableDelete={createUploadDocumentState.status === 'uploading'}
            />
          ) : (
            <StyledLinkRow>
              <StyledTextfieldWrapper>
                <TdsTextField
                  label='URL'
                  labelPosition='outside'
                  maxLength={600}
                  name='URL'
                  modeVariant='secondary'
                  placeholder='https//google.com'
                  value={data.url ?? ''}
                  onTdsInput={e => {
                    setData({ ...data, url: e.target.value })
                  }}
                  onTdsBlur={e => setStateUrl(ValidateUrl(e.target.value))}
                  state={!stateUrl ? 'error' : undefined}
                  helper={
                    !stateUrl
                      ? t('url-notValid', { ns: 'documents' })
                      : undefined
                  }
                />
              </StyledTextfieldWrapper>
              <TdsButton
                size='md'
                variant='secondary'
                text={t('open', { ns: 'common' })}
                onClick={() => data.url && window.open(data.url, '_blank')}
              />
            </StyledLinkRow>
          )}
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <Column width={12} lg={12} padding={isLg}>
          {createUploadDocumentState.status === 'error' && (
            <StyledMessage className='tds-u-mt1'>
              <TdsMessage
                header={createUploadDocumentState.message}
                variant={createUploadDocumentState.status}
              />
            </StyledMessage>
          )}
          {createDocumentState.status === 'error' && (
            <StyledMessage className='tds-u-mt1'>
              <TdsMessage
                header={createDocumentState.message}
                variant={createDocumentState.status}
              />
            </StyledMessage>
          )}
        </Column>
      </StyledRow>
      <StyledRow>
        <Column width={12} lg={12} padding={isLg}>
          <ButtonWrapper>
            <StyledFirstButton>
              <TdsButton
                text={t('cancel', {
                  ns: 'common'
                })}
                variant='secondary'
                onClick={() => navigate('/admin/documents/all')}
              />
            </StyledFirstButton>
            {createUploadDocumentState.status === 'uploading' ? (
              <SpinnerWrapper>
                <DelayedSpinner size='md' />
              </SpinnerWrapper>
            ) : (
              <StyledButton>
                <TdsButton
                  disabled={isLoading || isSuccess || !checkAllData()}
                  text={capitalizeFirstLetter(t('save', { ns: 'common' }))}
                  onClick={() => {
                    if (!isLoading && !isSuccess && validateAllData())
                      stateDocumentType === 'File'
                        ? handleClickUploadCreateDocument()
                        : handleClickCreateDocument()
                  }}
                />
              </StyledButton>
            )}
          </ButtonWrapper>
        </Column>
      </StyledRow>
    </Column>
  )
}
